import React, { useMemo } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import type { FieldValues, UseControllerProps } from "react-hook-form";
import { BillingAccountTypeFilter } from "client/api-v2";
import type { BillingAccountDto } from "client/api-v2";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { getBillingAccounts } from "../services/billingAccountService";

type BillingAccountSelectorProps<T extends FieldValues> = UseControllerProps<T> & {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  currentAccountCode?: string;
  hideResellers?: boolean;
};

export interface BillingAccountOption {
  id: string;
  code: string;
  name: string;
  isCurrentAccount: boolean;
}

function toOption(account: BillingAccountDto, currentAccountCode?: string): BillingAccountOption {
  return {
    id: account.code,
    code: account.code,
    name: account.commonName ?? account.legalName,
    isCurrentAccount: account.code === currentAccountCode,
  };
}

function makeSearchBillingAccounts(currentAccountCode?: string, hideResellers?: boolean) {
  return (searchTerm?: string) =>
    getBillingAccounts(
      {
        search: searchTerm,
        type: hideResellers ? BillingAccountTypeFilter.Customers : undefined,
        pageSize: 25,
      },
      new AbortController().signal
    ).then((response) => {
      const entities = response.items.map((item) => toOption(item, currentAccountCode));
      return { entities, count: entities.length, totalCount: response.totalItems };
    });
}

function noSearch() {
  return Promise.resolve({ entities: [], count: 0, totalCount: 0 });
}

function formatBillingAccountName(account: BillingAccountOption): string {
  return `${account.name} (${account.code})`;
}

function renderBillingAccountOption(account: BillingAccountOption) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%" }}>
      <Typography variant="body2">{formatBillingAccountName(account)}</Typography>
      {account.isCurrentAccount && <Chip label="On this account" size="small" variant="filled" color="primary" />}
    </Stack>
  );
}

function renderSelectorFooter(): React.ReactNode {
  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Type to search and see more results
      </Typography>
    </Box>
  );
}

export function BillingAccountSelector<T extends FieldValues>(props: BillingAccountSelectorProps<T>) {
  const { label, required, disabled, currentAccountCode, hideResellers, ...rest } = props;
  const searchBillingAccounts = useMemo(
    () => makeSearchBillingAccounts(currentAccountCode, hideResellers),
    [currentAccountCode, hideResellers]
  );

  return (
    <EntitySelector
      {...rest}
      disabled={disabled}
      label={label || "Billing account"}
      apiRequest={disabled ? noSearch : searchBillingAccounts}
      optionLabelFunc={formatBillingAccountName}
      renderOption={renderBillingAccountOption}
      required={required}
      renderFooter={renderSelectorFooter}
    />
  );
}
